// frontend/src/marketing/Seo.jsx
import { useEffect } from "react";
import PropTypes from "prop-types";

/**
 * Seo — dependency-free per-page <head> manager for the public marketing site.
 *
 * The app is client-rendered, so we set document.title + meta/OG tags imperatively
 * on mount. This intentionally avoids adding react-helmet-async until the
 * pre-rendering pipeline lands (see FRONTEND-PLAN.md F0-T3); at that point this
 * component can be swapped for a Helmet-based one with the same props.
 *
 * It upserts tags by a stable selector and tags everything it creates with
 * data-seo so a route change can leave existing static <head> tags untouched.
 */
const SITE_NAME = "QRFlow";
const DEFAULT_DESC =
  "QRFlow — dynamic QR codes with real-time, privacy-first analytics. Honest pricing, a developer-friendly API, and A/B testing. Start free.";
const DEFAULT_IMAGE = "/og-image.svg";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo="true"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({ title, description, path, image, jsonLd }) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
    const desc = description || DEFAULT_DESC;
    const url = path
      ? `${window.location.origin}${path}`
      : window.location.href;
    const imageUrl = `${window.location.origin}${image || DEFAULT_IMAGE}`;

    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imageUrl);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", imageUrl);

    // Optional JSON-LD structured data
    let script = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, path, jsonLd]);

  return null;
}

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
  image: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  jsonLd: PropTypes.object,
};

Seo.defaultProps = {
  title: undefined,
  description: undefined,
  path: undefined,
  image: undefined,
  jsonLd: undefined,
};
